// Figures that change by year. Edit here when new ones are published.
// Checked October 2026 against IRS Notice 2026-10 (and its July 2026 revision) and SSA wage-base announcements.
export const TAX_DATA_CHECKED = "October 2026";

// Business standard mileage rate, $ per mile, by effective date.
// 2026 was revised mid-year: 72.5¢ from Jan 1, 76¢ from Jul 1.
export const IRS_MILEAGE = [
  { from: "2023-01-01", rate: 0.655 },
  { from: "2024-01-01", rate: 0.67 },
  { from: "2025-01-01", rate: 0.70 },
  { from: "2026-01-01", rate: 0.725 },
  { from: "2026-07-01", rate: 0.76 },
];
export const SS_WAGE_BASE = { 2024: 168600, 2025: 176100, 2026: 184500 };

export function irsRateOn(dateISO) {
  let r = IRS_MILEAGE[0].rate;
  for (const e of IRS_MILEAGE) if (e.from <= dateISO) r = e.rate;
  return r;
}
export function irsRateKnownFor(dateISO) { return dateISO <= `${IRS_MILEAGE[IRS_MILEAGE.length - 1].from.slice(0, 4)}-12-31`; }
export function wageBase(year) {
  const ys = Object.keys(SS_WAGE_BASE).map(Number).sort((a, b) => a - b);
  return SS_WAGE_BASE[year] || SS_WAGE_BASE[year < ys[0] ? ys[0] : ys[ys.length - 1]];
}

// ---- official mileage rules by country -----------------------------------------------------------
// Only countries where a sole trader may deduct a flat per-distance rate are listed. Everywhere else (for example
// Canada, where the CRA per-km figure is an employer allowance, not a self-employed deduction) the user sets their own rate.
const MI = 1609.344;
export const OFFICIAL_MILEAGE = {
  US: { unit: "mi", name: "IRS standard mileage rate", rateOn: (d) => irsRateOn(d) },
  UK: { unit: "mi", name: "HMRC simplified mileage", taxYear: { m: 4, d: 6 }, tiers: [{ upTo: 10000, rate: 0.45 }, { upTo: Infinity, rate: 0.25 }] },
  AU: { unit: "km", name: "ATO cents per kilometre", taxYear: { m: 7, d: 1 }, rateOn: (d) => (d >= "2026-07-01" ? 0.91 : 0.88), capPerYear: 5000 },
};
const resolveCountry = (country, currency) => country || ((currency || "USD") === "USD" ? "US" : null);
export const hasOfficialMileage = (country, currency) => !!OFFICIAL_MILEAGE[resolveCountry(country, currency)];

// Does this profile use the official rate? An explicit choice wins; otherwise countries with an official rate use it
// unless the person already set their own rate (their choice is kept).
export function usesOfficialMileage(biz, currency, country) {
  const c = OFFICIAL_MILEAGE[resolveCountry(country, currency)];
  if (!c) return false;
  if (biz?.mileageMode === "official" || biz?.mileageMode === "irs") return true;
  if (biz?.mileageMode === "custom") return false;
  return (biz?.mileageRate === undefined || biz?.mileageRate === "") && (biz?.distanceUnit || c.unit) === c.unit;
}
export const usesIrsMileage = usesOfficialMileage;   // older name

const yearBucket = (cfg, date) => {
  if (!cfg.taxYear) return "all";
  const [y, m, d] = date.split("-").map(Number);
  return (m > cfg.taxYear.m || (m === cfg.taxYear.m && d >= cfg.taxYear.d)) ? y : y - 1;
};

// Deduction for every trip, in date order, as a Map(id -> amount). Handles tiers (UK) and yearly caps (AU) per tax year.
export function tripDeductions(trips, biz, currency, country) {
  const out = new Map();
  const key = resolveCountry(country, currency);
  const cfg = OFFICIAL_MILEAGE[key];
  const official = usesOfficialMileage(biz, currency, country);
  const sorted = (trips || []).slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const used = {};
  sorted.forEach((t, idx) => {
    const id = t.id ?? `i${idx}`;
    const dist = Number(t.distance) || 0;
    if (!official) { out.set(id, dist * (Number(biz?.mileageRate) || 0)); return; }
    const userUnit = biz?.distanceUnit || cfg.unit;
    let d = dist;                                           // convert to the unit the rate is quoted in
    if (userUnit !== cfg.unit) d = cfg.unit === "mi" ? dist * 1000 / MI : dist * MI / 1000;
    const bucket = yearBucket(cfg, t.date);
    const before = used[bucket] || 0;
    let amount = 0;
    if (cfg.tiers) {
      let prev = 0, left = d, at = before;
      for (const tier of cfg.tiers) {
        if (left <= 0) break;
        const room = Math.max(0, tier.upTo - Math.max(at, prev));
        const take = Math.min(left, room);
        amount += take * tier.rate; left -= take; at += take; prev = tier.upTo;
      }
    } else {
      const rate = cfg.rateOn(t.date);
      const room = cfg.capPerYear ? Math.max(0, cfg.capPerYear - before) : Infinity;
      amount = Math.min(d, room) * rate;
    }
    used[bucket] = before + d;
    out.set(id, amount);
  });
  return out;
}

// Total deduction, optionally only for trips inside a date range.
export function mileageDeductionFor(trips, biz, currency, country, range) {
  const m = tripDeductions(trips, biz, currency, country);
  return (trips || []).reduce((s, t, i) => {
    if (range && (t.date < range.start || t.date > range.end)) return s;
    return s + (m.get(t.id ?? `i${i}`) || 0);
  }, 0);
}

// Short human text of today's official rate, for the Mileage screen.
export function officialRateText(country, currency, today) {
  const key = resolveCountry(country, currency), c = OFFICIAL_MILEAGE[key];
  if (!c) return "";
  if (key === "US") return `${(irsRateOn(today) * 100).toFixed(1).replace(/\.0$/, "")}¢ per mile`;
  if (key === "UK") return "45p per mile for the first 10,000 miles each tax year, then 25p";
  if (key === "AU") return `${Math.round(c.rateOn(today) * 100)}c per km, up to 5,000 km per car each year`;
  return "";
}

// Estimated-tax payment periods are NOT calendar quarters: Jan–Mar, Apr–May, Jun–Aug, Sep–Dec.
const pad = (n) => String(n).padStart(2, "0");
export function taxPeriod(year, q) {
  const spans = { 1: [1, 3, 15, 4, 0], 2: [4, 5, 15, 6, 0], 3: [6, 8, 15, 9, 0], 4: [9, 12, 15, 1, 1] };
  const [m1, m2, d, dm, ny] = spans[q];
  const endDay = new Date(year, m2, 0).getDate();
  return { q, start: `${year}-${pad(m1)}-01`, end: `${year}-${pad(m2)}-${pad(endDay)}`, due: nextBusinessDay(year + ny, dm, d), label: `Q${q}` };
}
// Weekend shift only. Federal holidays can also move a deadline — the screen says to check.
export function nextBusinessDay(y, m, d) {
  const dt = new Date(y, m - 1, d);
  while (dt.getDay() === 0 || dt.getDay() === 6) dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
